import { useId, type ReactElement, type ReactNode } from 'react';
import { Button as BaseButton } from '@base-ui/react/button';
import { Checkbox as BaseCheckbox } from '@base-ui/react/checkbox';
import { Dialog as BaseDialog } from '@base-ui/react/dialog';
import { Field as BaseField } from '@base-ui/react/field';
import { Menu as BaseMenu } from '@base-ui/react/menu';
import { Popover as BasePopover } from '@base-ui/react/popover';
import { Tabs as BaseTabs } from '@base-ui/react/tabs';
import { Toast as BaseToast } from '@base-ui/react/toast';
import { Tooltip as BaseTooltip } from '@base-ui/react/tooltip';
import clsx from 'clsx';
import { Check, X } from '@phosphor-icons/react';
import styles from './components.module.css';

export { MarqueeText } from './MarqueeText';

type BaseButtonProps = React.ComponentProps<typeof BaseButton>;

export function Button({
  variant = 'default',
  size = 'default',
  iconOnly,
  className,
  ...props
}: BaseButtonProps & {
  variant?: 'default' | 'primary' | 'ghost' | 'danger';
  size?: 'default' | 'small';
  iconOnly?: boolean;
}) {
  return (
    <BaseButton
      className={clsx(
        styles.button,
        variant === 'primary' && styles.buttonPrimary,
        variant === 'ghost' && styles.buttonGhost,
        variant === 'danger' && styles.buttonDanger,
        size === 'small' && styles.buttonSmall,
        iconOnly && styles.buttonIcon,
        className,
      )}
      {...props}
    />
  );
}

export function TextField({
  label,
  description,
  error,
  ...props
}: Omit<React.ComponentProps<typeof BaseField.Control>, 'className'> & {
  label: string;
  description?: string;
  error?: string;
}) {
  return (
    <BaseField.Root className={styles.field} invalid={Boolean(error)}>
      <BaseField.Label className={styles.label}>{label}</BaseField.Label>
      <BaseField.Control className={styles.input} {...props} />
      {description && (
        <BaseField.Description className={styles.description}>{description}</BaseField.Description>
      )}
      {error && <BaseField.Error className={styles.error}>{error}</BaseField.Error>}
    </BaseField.Root>
  );
}

export function TextArea({
  label,
  description,
  error,
  ...props
}: React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label: string;
  description?: string;
  error?: string;
}) {
  const id = useId();
  return (
    <BaseField.Root className={clsx(styles.field, styles.textareaField)} invalid={Boolean(error)}>
      <BaseField.Label className={styles.label} htmlFor={id}>
        {label}
      </BaseField.Label>
      <textarea id={id} className={styles.textarea} {...props} />
      {description && (
        <BaseField.Description className={styles.description}>{description}</BaseField.Description>
      )}
      {error && <BaseField.Error className={styles.error}>{error}</BaseField.Error>}
    </BaseField.Root>
  );
}

export function Dialog({
  trigger,
  title,
  description,
  titleIcon,
  children,
  headerActions,
  actions,
  open,
  onOpenChange,
  size = 'default',
  className,
  bodyClassName,
  viewportClassName,
  presentation = 'dialog',
}: {
  trigger?: ReactElement;
  title: string;
  description?: string;
  titleIcon?: ReactNode;
  children: ReactNode;
  headerActions?: ReactNode;
  actions?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  size?: 'default' | 'large';
  className?: string;
  bodyClassName?: string;
  viewportClassName?: string;
  presentation?: 'dialog' | 'sheet';
}) {
  return (
    <BaseDialog.Root open={open} onOpenChange={onOpenChange}>
      {trigger && <BaseDialog.Trigger render={trigger} />}
      <BaseDialog.Portal>
        <BaseDialog.Backdrop className={styles.backdrop} />
        <BaseDialog.Viewport
          className={clsx(
            styles.positioner,
            presentation === 'sheet' && styles.sheetPositioner,
            viewportClassName,
          )}
        >
          <BaseDialog.Popup
            className={clsx(
              styles.dialog,
              size === 'large' && styles.dialogLarge,
              presentation === 'sheet' && styles.dialogSheet,
              className,
            )}
          >
            <header className={styles.dialogHeader}>
              <div className={styles.dialogHeading}>
                <div className={styles.dialogTitleRow}>
                  {titleIcon ? <span className={styles.dialogTitleIcon}>{titleIcon}</span> : null}
                  <div className={styles.dialogTitleBlock}>
                    <BaseDialog.Title className={styles.dialogTitle} title={title}>
                      {title}
                    </BaseDialog.Title>
                    {description ? (
                      <BaseDialog.Description className={styles.dialogDescription}>
                        {description}
                      </BaseDialog.Description>
                    ) : null}
                  </div>
                </div>
              </div>
              {headerActions && <div className={styles.dialogHeaderActions}>{headerActions}</div>}
              <BaseDialog.Close className={styles.dialogClose} aria-label="Close dialog">
                <X size={15} />
              </BaseDialog.Close>
            </header>
            <div className={clsx(styles.dialogBody, bodyClassName)}>{children}</div>
            {actions && <footer className={styles.dialogActions}>{actions}</footer>}
          </BaseDialog.Popup>
        </BaseDialog.Viewport>
      </BaseDialog.Portal>
    </BaseDialog.Root>
  );
}

export const DialogClose = BaseDialog.Close;

export function ActionMenu({
  trigger,
  header,
  items,
}: {
  trigger: ReactElement;
  header?: ReactNode;
  items: Array<{
    label: string;
    onClick: () => void;
    danger?: boolean;
    separator?: boolean;
    trailing?: ReactNode;
  }>;
}) {
  return (
    <BaseMenu.Root>
      <BaseMenu.Trigger render={trigger} />
      <BaseMenu.Portal>
        <BaseMenu.Positioner className={styles.menuPositioner} sideOffset={5} align="end">
          <BaseMenu.Popup className={styles.menu}>
            {header ? <div className={styles.menuHeader}>{header}</div> : null}
            {items.map((item) =>
              item.separator ? (
                <BaseMenu.Separator key={item.label} className={styles.menuSeparator} />
              ) : (
                <BaseMenu.Item
                  key={item.label}
                  className={clsx(styles.menuItem, item.danger && styles.menuItemDanger)}
                  onClick={item.onClick}
                >
                  {item.label}
                  {item.trailing ? (
                    <span className={styles.menuItemTrailing}>{item.trailing}</span>
                  ) : null}
                </BaseMenu.Item>
              ),
            )}
          </BaseMenu.Popup>
        </BaseMenu.Positioner>
      </BaseMenu.Portal>
    </BaseMenu.Root>
  );
}

export function Tabs({
  tabs,
  defaultValue,
}: {
  tabs: Array<{ value: string; label: string; content: ReactNode }>;
  defaultValue?: string;
}) {
  return (
    <BaseTabs.Root defaultValue={defaultValue ?? tabs[0]?.value}>
      <BaseTabs.List className={styles.tabsList}>
        {tabs.map((tab) => (
          <BaseTabs.Tab key={tab.value} value={tab.value} className={styles.tab}>
            {tab.label}
          </BaseTabs.Tab>
        ))}
        <BaseTabs.Indicator className={styles.tabsIndicator} />
      </BaseTabs.List>
      {tabs.map((tab) => (
        <BaseTabs.Panel key={tab.value} value={tab.value} className={styles.tabPanel}>
          {tab.content}
        </BaseTabs.Panel>
      ))}
    </BaseTabs.Root>
  );
}

export function Tooltip({ content, children }: { content: ReactNode; children: ReactElement }) {
  return (
    <BaseTooltip.Provider>
      <BaseTooltip.Root>
        <BaseTooltip.Trigger render={children} />
        <BaseTooltip.Portal>
          <BaseTooltip.Positioner className={styles.tooltipPositioner} sideOffset={6}>
            <BaseTooltip.Popup className={styles.tooltip}>{content}</BaseTooltip.Popup>
          </BaseTooltip.Positioner>
        </BaseTooltip.Portal>
      </BaseTooltip.Root>
    </BaseTooltip.Provider>
  );
}

export function Popover({
  trigger,
  children,
  open,
  onOpenChange,
  side = 'bottom',
  align = 'start',
  sideOffset = 6,
}: {
  trigger: ReactElement;
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  side?: 'top' | 'bottom' | 'left' | 'right';
  align?: 'start' | 'center' | 'end';
  sideOffset?: number;
}) {
  return (
    <BasePopover.Root open={open} onOpenChange={onOpenChange}>
      <BasePopover.Trigger render={trigger} />
      <BasePopover.Portal>
        <BasePopover.Positioner
          className={styles.popoverPositioner}
          side={side}
          align={align}
          sideOffset={sideOffset}
        >
          <BasePopover.Popup className={styles.popover}>{children}</BasePopover.Popup>
        </BasePopover.Positioner>
      </BasePopover.Portal>
    </BasePopover.Root>
  );
}

export function Checkbox({
  label,
  ...props
}: React.ComponentProps<typeof BaseCheckbox.Root> & { label?: string }) {
  const control = (
    <BaseCheckbox.Root className={styles.checkbox} {...props}>
      <BaseCheckbox.Indicator>
        <Check size={12} weight="bold" />
      </BaseCheckbox.Indicator>
    </BaseCheckbox.Root>
  );

  if (!label) return control;

  return (
    <label className={styles.checkboxLabel}>
      {control}
      {label}
    </label>
  );
}

export const toastManager = BaseToast.createToastManager();

function ToastList() {
  const { toasts } = BaseToast.useToastManager();
  return toasts.map((toast) => (
    <BaseToast.Root key={toast.id} toast={toast} className={styles.toast}>
      <BaseToast.Content className={styles.toastContent}>
        <div className={styles.toastText}>
          <BaseToast.Title className={styles.toastTitle} />
          <BaseToast.Description className={styles.toastDescription} />
        </div>
        <BaseToast.Close className={styles.toastClose} aria-label="Dismiss">
          <X size={14} />
        </BaseToast.Close>
      </BaseToast.Content>
    </BaseToast.Root>
  ));
}

export function ToastProvider({ children }: { children: ReactNode }) {
  return (
    <BaseToast.Provider toastManager={toastManager}>
      {children}
      <BaseToast.Portal>
        <BaseToast.Viewport className={styles.toastViewport}>
          <ToastList />
        </BaseToast.Viewport>
      </BaseToast.Portal>
    </BaseToast.Provider>
  );
}
